import React, { forwardRef, isValidElement, useState } from 'react'
import FieldContainer from "./components/FieldContainer"
import PropTypes from 'prop-types'
import credible3 from './Radio.module.scss'
import cls from 'classnames'
import Font from '@cred/react-font'
import { fieldContainerProps, fieldProps, analyticsProps } from './commonProps'
import useInternalState from './components/useInternalState'
import Radio from './Radio'

const RadioGroup = forwardRef(({
  id,
  error,
  warning,
  touched,
  focus,
  name,
  initialValue,
  className,
  inputRef,
  inputProps,
  disabled,
  display,
  borderType,
  uncheckedIcon,
  checkedIcon,
  children,
  analytics,
  autoFocus,
  autoFocusDelay,
  appearance
}, ref) => {
  const [value, setValue] = useState(initialValue || ''),
    radioInputProps = {
      onChange(evt) {
        if (evt.target.checked) {
          setValue(evt.target.value)
          if (inputProps.onChange) {
            inputProps.onChange(evt.target.value)
          }
        }
      }
    },
    { internalFocus, internalTouched, internalInputProps } = useInternalState({ focus, touched, inputProps: radioInputProps })

  if (!Array.isArray(children)) {
    throw 'The RadioGroup accepts only an array of <option> as children'
  }

  return (
    <FieldContainer ref={ref} id={id} error={error} warning={warning} touched={internalTouched} focus={internalFocus} className={className} appearance={appearance}>
      <input
        value={value}
        data-testid="hiddenElement"
        {...inputProps}
        type="hidden"
        id={id}
        name={name}
        ref={inputRef}
        disabled={disabled}
      />
      <div>
        {children.map((option, index) => {
          if (!isValidElement(option) || !option.props.value || !option.props.label) {
            throw 'The RadioGroup only accepts <option> with a value and a label'
          }

          return (
            <Radio
              id={`${id}-${index}`}
              key={`${id}-${index}`}
              name={name}
              label={option.props.label}
              initialChecked={initialValue === option.props.value}
              inputProps={{
                ...internalInputProps,
                checked: inputProps.value === undefined ? value === option.props.value : inputProps.value === option.props.value
              }}
              disabled={disabled}
              display={display}
              borderType={borderType}
              description={option.props.children}
              value={option.props.value}
              uncheckedIcon={uncheckedIcon}
              checkedIcon={checkedIcon}
              className={cls({ 'mb-16': appearance !== 'compact' })}
              analytics={analytics}
              autoFocus={index === 0 && autoFocus}
              autoFocusDelay={autoFocusDelay}
              appearance={appearance}
            />
          )
        })}
      </div>
    </FieldContainer>
  )
})

RadioGroup.propTypes = {
  ...fieldContainerProps,
  ...fieldProps,
  ...analyticsProps,
  /** The initial value to be displayed in the input */
  initialValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Sets the kind of display the component should have between grouped and block, grouped radios must be siblings to stick together */
  display: PropTypes.oneOf(['grouped', 'block']),
  /** The border type the component should have between noBorder or solid */
  borderType: PropTypes.oneOf(['noBorder', 'solid']),
  /** Icon to be displayed in to the right of the label while the field is unchecked */
  uncheckedIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Icon to be displayed in to the right of the label when the field is checked */
  checkedIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** default: regular appearance used in prequal forms; compact: smaller inputs to fit smaller forms and widgets */
  appearance: PropTypes.oneOf(['default', 'compact'])
}

RadioGroup.defaultProps = {
  error: null,
  warning: null,
  touched: null,
  focus: null,
  inputRef: null,
  inputProps: {},
  disabled: false,
  display: 'block',
  borderType: 'solid',
  uncheckedIcon: 'icon-chevron-right',
  checkedIcon: <Font element="i" type="medium" className={cls('cred-icon', 'icon-check-circle', credible3.checkedIcon)}/>,
  autoFocus: false,
  autoFocusDelay: 500,
  appearance: 'default'
}

export default RadioGroup
